import { Task, TaskType } from "./types";

export type LiveTaskTarget = {
  activeTask: Task;
  navigateTask: Task;
  timerStartedAt: string | null;
};

export const SERVICE_BUNDLE_CHILD_TYPES = new Set<TaskType>([
  "machine_refill",
  "machine_clean",
  "machine_collection",
]);

export const SERVICE_BUNDLE_DISPLAY_ORDER: Record<TaskType, number> = {
  machine_service: 0,
  machine_collection: 1,
  machine_refill: 2,
  machine_clean: 3,
  machine_pickup_inventory: 4,
  default: 99,
  machine_remove: 99,
  machine_install: 99,
  machine_repair: 99,
  machine_refund: 99,
  location_lead: 99,
  other: 99,
};

export const isServiceBundleChildTaskType = (type?: string | null): boolean =>
  SERVICE_BUNDLE_CHILD_TYPES.has((type || "") as TaskType);

export const compareServiceBundleTasks = (left: Task, right: Task): number => {
  const leftOrder = SERVICE_BUNDLE_DISPLAY_ORDER[left.type] ?? 99;
  const rightOrder = SERVICE_BUNDLE_DISPLAY_ORDER[right.type] ?? 99;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;
  return (left.createdAt || "").localeCompare(right.createdAt || "");
};

export const getTaskBundleKey = (
  task: Pick<Task, "id" | "type" | "serviceTaskId">,
): string => {
  if (task.type === "machine_service" && task.id) {
    return `service:${task.id}`;
  }
  if (task.serviceTaskId) {
    return `service:${task.serviceTaskId}`;
  }
  return "";
};

export const getServiceBundleGroupKey = (
  task: Pick<
    Task,
    "id" | "type" | "serviceTaskId"
  >,
): string => {
  return getTaskBundleKey(task);
};

export const getBundledTasksForService = (
  tasks: Task[],
  serviceTask: Task,
): Task[] => {
  if (serviceTask.type !== "machine_service") return [];
  const explicitMatches = tasks.filter(
    (task) =>
      task.id !== serviceTask.id &&
      isServiceBundleChildTaskType(task.type) &&
      task.serviceTaskId === serviceTask.id,
  );
  if (explicitMatches.length) {
    return explicitMatches.sort(compareServiceBundleTasks);
  }
  return [];
};

export const getServiceTaskForBundledTask = (
  tasks: Task[],
  task: Task,
): Task | null => {
  if (!isServiceBundleChildTaskType(task.type)) return null;
  if (task.serviceTaskId) {
    return tasks.find((entry) => entry.id === task.serviceTaskId) || null;
  }
  return null;
};

const uniqueTasks = (tasks: Task[]): Task[] =>
  Array.from(new Map(tasks.map((task) => [task.id, task])).values());

export const getPickupTasksForRefill = (
  tasks: Task[],
  refillTask: Task,
): Task[] => {
  if (refillTask.type !== "machine_refill") return [];
  return tasks
    .filter(
      (entry) =>
        entry.type === "machine_pickup_inventory" &&
        (entry.refillTaskId === refillTask.id ||
          (entry.refillTaskIds || []).includes(refillTask.id) ||
          (entry.pickupLines || []).some(
            (line) => line.refillTaskId === refillTask.id,
          )),
    )
    .sort(compareServiceBundleTasks);
};

const getRefillTasksForPickup = (tasks: Task[], pickupTask: Task): Task[] => {
  if (pickupTask.type !== "machine_pickup_inventory") return [];
  const refillIds = new Set(
    [
      pickupTask.refillTaskId,
      ...(pickupTask.refillTaskIds || []),
      ...(pickupTask.pickupLines || []).map((line) => line.refillTaskId),
    ].filter((taskId): taskId is string => Boolean(taskId)),
  );
  return tasks.filter(
    (entry) => entry.type === "machine_refill" && refillIds.has(entry.id),
  );
};

const getPickupTasksForService = (tasks: Task[], serviceTask: Task): Task[] => {
  if (serviceTask.type !== "machine_service") return [];
  return uniqueTasks(
    getBundledTasksForService(tasks, serviceTask).flatMap((entry) =>
      entry.type === "machine_refill" ? getPickupTasksForRefill(tasks, entry) : [],
    ),
  ).sort(compareServiceBundleTasks);
};

export const getConnectedServiceBundleTasks = (
  tasks: Task[],
  task: Task,
): Task[] => {
  if (task.type === "machine_service") {
    return uniqueTasks([
      ...getBundledTasksForService(tasks, task),
      ...getPickupTasksForService(tasks, task),
    ]).sort(compareServiceBundleTasks);
  }
  if (isServiceBundleChildTaskType(task.type)) {
    const serviceTask = getServiceTaskForBundledTask(tasks, task);
    const pickupTasks =
      task.type === "machine_refill" ? getPickupTasksForRefill(tasks, task) : [];
    return uniqueTasks([...(serviceTask ? [serviceTask] : []), ...pickupTasks]).sort(
      compareServiceBundleTasks,
    );
  }
  if (task.type === "machine_pickup_inventory") {
    const refillTasks = getRefillTasksForPickup(tasks, task);
    const serviceTasks = refillTasks
      .map((refillTask) => getServiceTaskForBundledTask(tasks, refillTask))
      .filter((entry): entry is Task => Boolean(entry));
    return uniqueTasks([
      ...serviceTasks,
      ...refillTasks,
    ]).sort(compareServiceBundleTasks);
  }
  return [];
};

export const getUnifiedServiceBundleTasks = (
  tasks: Task[],
  task: Task,
): Task[] => {
  if (task.type === "machine_service") {
    return [task, ...getBundledTasksForService(tasks, task)].sort(
      compareServiceBundleTasks,
    );
  }
  if (isServiceBundleChildTaskType(task.type)) {
    const serviceTask = getServiceTaskForBundledTask(tasks, task);
    if (!serviceTask) return [task];
    return [serviceTask, ...getBundledTasksForService(tasks, serviceTask)].sort(
      compareServiceBundleTasks,
    );
  }
  return [task];
};

const getActiveWorkScopeForStartedTask = (
  tasks: Task[],
  activeTask: Task,
): Task[] => {
  const byId = new Map<string, Task>();
  const add = (task?: Task | null) => {
    if (task?.id) byId.set(task.id, task);
  };
  const addServiceBundle = (task: Task) => {
    getUnifiedServiceBundleTasks(tasks, task).forEach(add);
  };

  add(activeTask);
  addServiceBundle(activeTask);

  if (activeTask.type === "machine_pickup_inventory") {
    getRefillTasksForPickup(tasks, activeTask).forEach((refillTask) => {
      add(refillTask);
      addServiceBundle(refillTask);
    });
  }

  return Array.from(byId.values()).sort(compareServiceBundleTasks);
};

const isActiveInProgressTask = (
  task?: Pick<Task, "type" | "isLive" | "startedAt" | "status" | "doneAt"> | null,
) => Boolean(task && (task.isLive || (task.type !== "machine_service" && task.startedAt)) && !task.doneAt && !isTaskFinal(task));

const isFinalTaskStatus = (status?: string | null) =>
  status === "done" || status === "cancelled" || status === "error";

const isTaskFinal = (task?: Pick<Task, "status"> | null) =>
  isFinalTaskStatus(task?.status);

const getStartedAtTime = (task?: Pick<Task, "startedAt"> | null) => {
  const value = task?.startedAt ? new Date(task.startedAt).getTime() : 0;
  return Number.isNaN(value) ? 0 : value;
};

const getEarliestActiveStartedAt = (tasks: Task[]): string | null => {
  const activeTasks = tasks.filter(isActiveInProgressTask);
  if (!activeTasks.length) return null;
  const earliest = [...activeTasks].sort(
    (left, right) => getStartedAtTime(left) - getStartedAtTime(right),
  )[0];
  return earliest?.startedAt || null;
};

const getEarliestStartedAt = (tasks: Task[]): string | null => {
  const startedTasks = tasks.filter((task) => Boolean(task.startedAt));
  if (!startedTasks.length) return null;
  const earliest = [...startedTasks].sort(
    (left, right) => getStartedAtTime(left) - getStartedAtTime(right),
  )[0];
  return earliest?.startedAt || null;
};

const hasIncompleteBundleWork = (tasks: Task[]) =>
  tasks.some(
    (task) =>
      task.type !== "machine_service" &&
      task.type !== "machine_pickup_inventory" &&
      !isTaskFinal(task),
  );

const getStartedServiceBundleTarget = (
  scopedTaskIds: Set<string>,
  allTasks: Task[],
): LiveTaskTarget | null => {
  const serviceTasks = allTasks
    .filter(
      (task) =>
        task.type === "machine_service" &&
        !isTaskFinal(task) &&
        Boolean(task.startedAt) &&
        !task.doneAt,
    )
    .sort((left, right) => getStartedAtTime(right) - getStartedAtTime(left));

  for (const serviceTask of serviceTasks) {
    const bundleTasks = getUnifiedServiceBundleTasks(allTasks, serviceTask);
    if (!bundleTasks.some((entry) => scopedTaskIds.has(entry.id))) continue;
    if (!hasIncompleteBundleWork(bundleTasks)) continue;
    const timerStartedAt = getEarliestStartedAt(bundleTasks);
    if (!timerStartedAt) continue;
    const activeTask =
      bundleTasks.find(isActiveInProgressTask) ||
      bundleTasks.find((entry) => entry.type !== "machine_service" && !isTaskFinal(entry)) ||
      serviceTask;
    return {
      activeTask,
      navigateTask: serviceTask,
      timerStartedAt,
    };
  }

  return null;
};

export const isTaskEffectivelyInProgress = (
  task: Task,
  allTasks: Task[],
): boolean =>
  allTasks
    .filter(isActiveInProgressTask)
    .some((activeTask) =>
      getActiveWorkScopeForStartedTask(allTasks, activeTask).some(
        (entry) => entry.id === task.id,
      ),
    );

export const getLiveTaskTarget = (
  scopedTasks: Task[],
  allTasks: Task[] = scopedTasks,
): LiveTaskTarget | null => {
  const scopedTaskIds = new Set(scopedTasks.map((task) => task.id));
  const ordered = [...allTasks].sort(
    (left, right) => getStartedAtTime(right) - getStartedAtTime(left),
  );
  const seen = new Set<string>();

  for (const task of ordered) {
    const groupKey = getServiceBundleGroupKey(task) || `task:${task.id}`;
    if (seen.has(groupKey)) continue;
    const activeTask = isActiveInProgressTask(task) ? task : null;
    if (!activeTask) continue;
    const activeScopeTasks = getActiveWorkScopeForStartedTask(allTasks, activeTask);
    if (!activeScopeTasks.some((entry) => scopedTaskIds.has(entry.id))) continue;
    seen.add(groupKey);

    const serviceTask =
      activeTask.type === "machine_pickup_inventory"
        ? null
        : activeScopeTasks.find((entry) => entry.type === "machine_service") || null;
    return {
      activeTask,
      navigateTask: serviceTask || activeTask,
      timerStartedAt: getEarliestActiveStartedAt(activeScopeTasks),
    };
  }

  return getStartedServiceBundleTarget(scopedTaskIds, allTasks);
};

export const expandTasksToServiceBundles = (
  scopedTasks: Task[],
  filteredTasks: Task[],
): Task[] => {
  const serviceIds = new Set(scopedTasks
    .filter((task) => task.type === "machine_service")
    .map((task) => task.id));
  const selectedIds = new Set(filteredTasks.map((task) => task.id));
  const selectedServices = new Set<string>();
  filteredTasks.forEach((task) => {
    if (task.type === "machine_service") selectedServices.add(task.id);
    else if (isServiceBundleChildTaskType(task.type) && task.serviceTaskId && serviceIds.has(task.serviceTaskId)) {
      selectedServices.add(task.serviceTaskId);
    }
  });
  return scopedTasks.filter((task) => selectedIds.has(task.id) ||
    (task.type === "machine_service" && selectedServices.has(task.id)) ||
    (isServiceBundleChildTaskType(task.type) && Boolean(task.serviceTaskId) && selectedServices.has(task.serviceTaskId!)));
};
